import React, { useCallback, useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  ActivityIndicator,
  Platform,
  useWindowDimensions,
} from 'react-native';
import Feather from '@expo/vector-icons/Feather';
import { useDispatch } from 'react-redux';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { loginUser } from '../../src/redux/authSlice';
import type { AppDispatch } from '../../src/redux/store';
import { verifyResetCode, confirmNewPassword } from '../../src/services/authService';
import { trackEvent } from '../../src/services/analyticsService';
import {
  MIN_PASSWORD_LENGTH,
  readResetCode,
  resetLinkErrorKind,
  takeHandedOffResetCode,
} from '../../src/utils/passwordResetLink';
import { Paprika } from '../../src/styles/theme';
import { BrandRow } from '../../src/components/onboarding/BrandRow';
import {
  BrandPanel,
  AuthField,
  PrimaryButton,
  AlertBanner,
  SuccessPanel,
  type AlertTone,
} from '../../src/components/auth';

const DESKTOP_BREAKPOINT = 820;

type Step = 'checking' | 'form' | 'invalid' | 'done';

type BannerState = {
  tone: AlertTone;
  icon: React.ComponentProps<typeof Feather>['name'];
  title: string;
  message?: string;
  actionLabel?: string;
  onAction?: () => void;
} | null;

/**
 * Route /auth/reset?oobCode=… — where Du Chef's reset email lands
 * (api/_lib/passwordReset.ts). It replaces Firebase's hosted page on
 * paprika-app-1f7b0.firebaseapp.com: the cook checks the link, picks a new
 * password, and is signed in with it.
 */
export default function ResetPasswordScreen() {
  const { t } = useTranslation();
  const router = useRouter();
  const dispatch = useDispatch<AppDispatch>();
  const { oobCode } = useLocalSearchParams<{ oobCode?: string }>();
  // The code is a one-hour key to the account. On the web, public/index.html
  // took it out of the address bar before the app loaded, so the router,
  // PostHog and the recorder never saw it; the parameter is the fallback.
  const [code] = useState(() => takeHandedOffResetCode() ?? readResetCode(oobCode));
  // The fallback's cleanup. Deferred: a screen's effects run before the
  // navigation container's, and the container writes the URL from its own state
  // (code included) on mount. expo-router's setParams cannot drop a key, so the
  // bar is rewritten by hand once the container has settled.
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;
    const timer = setTimeout(() => {
      const url = new URL(window.location.href);
      if (!url.searchParams.has('oobCode')) return;
      url.searchParams.delete('oobCode');
      window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
    }, 0);
    return () => clearTimeout(timer);
  }, []);
  const { width } = useWindowDimensions();
  const isDesktop = width >= DESKTOP_BREAKPOINT;

  const [step, setStep] = useState<Step>('checking');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [banner, setBanner] = useState<BannerState>(null);
  const [saving, setSaving] = useState(false);

  const offlineBanner = (retry: () => void): BannerState => ({
    tone: 'danger',
    icon: 'wifi-off',
    title: t('auth.errOfflineTitle'),
    message: t('auth.errOfflineMsg'),
    actionLabel: t('auth.retry'),
    onAction: retry,
  });

  const check = useCallback(async () => {
    setBanner(null);
    if (!code) {
      setStep('invalid');
      trackEvent('password_reset_link_invalid', { reason: 'missing' });
      return;
    }
    setStep('checking');
    try {
      setEmail(await verifyResetCode(code));
      setStep('form');
    } catch (error) {
      const kind = resetLinkErrorKind(error);
      // An expired or spent link is routine: the code alone goes to the replay.
      console.warn('[reset] link check failed', (error as { code?: string })?.code);
      if (kind === 'network') {
        setStep('form');
        setBanner(offlineBanner(check));
        return;
      }
      setStep('invalid');
      trackEvent('password_reset_link_invalid', { reason: (error as { code?: string })?.code ?? 'unknown' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  useEffect(() => {
    check();
  }, [check]);

  const handleSave = async () => {
    setFieldError(null);
    setBanner(null);
    if (!code) return;
    if (password.length < MIN_PASSWORD_LENGTH) {
      setFieldError(t('auth.validationPasswordLength'));
      return;
    }
    setSaving(true);
    try {
      await confirmNewPassword(code, password);
    } catch (error) {
      setSaving(false);
      const kind = resetLinkErrorKind(error);
      if (kind === 'unknown') console.error('[reset] new password not saved', error);
      else console.warn('[reset] new password refused', (error as { code?: string })?.code);
      if (kind === 'weak-password') setFieldError(t('auth.validationPasswordLength'));
      else if (kind === 'invalid') setStep('invalid');
      else if (kind === 'network') setBanner(offlineBanner(handleSave));
      else setBanner({ tone: 'danger', icon: 'alert-triangle', title: t('auth.resetLink.failed') });
      return;
    }
    trackEvent('password_reset_completed');
    try {
      await dispatch(loginUser({ email, password })).unwrap();
      setStep('done');
    } catch (error) {
      // The password is changed; only the sign-in after it failed.
      console.error('[reset] sign-in after reset failed', error);
      setBanner({
        tone: 'info',
        icon: 'check',
        title: t('auth.resetLink.savedSignIn'),
        actionLabel: t('auth.resetLink.signIn'),
        onAction: () => router.replace('/auth'),
      });
    } finally {
      setSaving(false);
    }
  };

  let content: React.ReactNode;
  if (step === 'done') {
    content = <SuccessPanel onOpen={() => router.replace('/(app)')} />;
  } else if (step === 'checking') {
    content = (
      <View style={styles.checking} testID="reset-checking">
        <ActivityIndicator color={Paprika.accent} />
        <Text style={styles.muted}>{t('auth.resetLink.checking')}</Text>
      </View>
    );
  } else if (step === 'invalid') {
    content = (
      <View testID="reset-invalid">
        <Text style={styles.h1}>{t('auth.resetLink.invalidTitle')}</Text>
        <Text style={styles.body}>{t('auth.resetLink.invalidMsg')}</Text>
        <PrimaryButton label={t('auth.resetLink.newLink')} onPress={() => router.replace('/auth')} />
      </View>
    );
  } else {
    content = (
      <View testID="reset-form">
        <Text style={styles.h1}>{t('auth.resetLink.title')}</Text>
        {email ? (
          <View style={styles.chip}>
            <Feather name="mail" size={16} color={Paprika.textMuted} />
            <Text style={styles.chipEmail} numberOfLines={1} testID="reset-email">
              {email}
            </Text>
          </View>
        ) : null}
        {banner ? <View style={styles.bannerWrap}><AlertBanner {...banner} /></View> : null}
        <View style={styles.stack}>
          <AuthField
            icon="lock"
            label={t('auth.resetLink.newPassword')}
            value={password}
            onChangeText={setPassword}
            password
            error={fieldError}
            autoFocus
            onSubmitEditing={handleSave}
          />
          <PrimaryButton
            label={t('auth.resetLink.save')}
            onPress={handleSave}
            loading={saving}
            disabled={!email}
          />
        </View>
      </View>
    );
  }

  const formColumn = <View style={styles.formInner}>{content}</View>;

  if (isDesktop) {
    return (
      <View style={styles.desktopShell}>
        <View style={styles.brandCol}>
          <BrandPanel />
        </View>
        <View style={styles.formColDesktop}>{formColumn}</View>
      </View>
    );
  }

  return (
    <View style={styles.mobileShell}>
      <ScrollView
        contentContainerStyle={styles.mobileScroll}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        {/* The cook just came from an email: say whose screen this is. */}
        <View style={styles.mobileBrand}>
          <BrandRow />
        </View>
        {formColumn}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  desktopShell: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: Paprika.background,
  },
  brandCol: {
    flex: 0.92,
  },
  formColDesktop: {
    flex: 1.08,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
  },
  mobileShell: {
    flex: 1,
    backgroundColor: Paprika.background,
  },
  mobileScroll: {
    flexGrow: 1,
    justifyContent: 'center',
    paddingHorizontal: 24,
    paddingVertical: 32,
  },
  mobileBrand: {
    width: '100%',
    maxWidth: 380,
    alignSelf: 'center',
    marginBottom: 40,
  },
  formInner: {
    width: '100%',
    maxWidth: 380,
    alignSelf: 'center',
  },
  h1: {
    fontFamily: 'Newsreader-Regular',
    fontSize: 30,
    lineHeight: 33,
    letterSpacing: -0.3,
    color: Paprika.textPrimary,
    marginBottom: 22,
  },
  body: {
    fontFamily: 'Inter-Regular',
    fontSize: 15,
    lineHeight: 22,
    color: Paprika.textMuted,
    marginBottom: 24,
  },
  muted: {
    fontFamily: 'Inter-Regular',
    fontSize: 14,
    color: Paprika.textMuted,
  },
  checking: {
    alignItems: 'center',
    gap: 14,
    paddingVertical: 40,
  },
  stack: {
    gap: 14,
  },
  bannerWrap: {
    marginBottom: 16,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
    paddingVertical: 10,
    paddingHorizontal: 14,
    backgroundColor: Paprika.surfaceLow,
    borderRadius: 12,
    marginBottom: 18,
  },
  chipEmail: {
    flex: 1,
    fontFamily: 'Inter-Medium',
    fontSize: 14,
    color: Paprika.textPrimary,
  },
});
